import { ReactNode } from "react";
import { Alert, Avatar, Box, Button, IconButton, InputAdornment, Stack, SxProps, TextField, Theme, Typography } from "@mui/material";
import { Clear, Search } from "@mui/icons-material";

import { getErrorMessage } from "../orders/orderUtils";

// Shared building blocks for the Set up pages (Services, Shipment profiles, Customers).

export const PageHeader = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
}) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    justifyContent="space-between"
    alignItems={{ xs: "flex-start", sm: "center" }}
    gap={1.5}
    mb={2.5}
  >
    <Box>
      <Typography component="h1" fontSize={20} fontWeight={700} color="#1b2533">
        {title}
      </Typography>
      {subtitle && (
        <Typography fontSize={14} color="text.secondary" mt={0.25}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {action}
  </Stack>
);

export const SearchField = ({
  value,
  onChange,
  placeholder,
  sx,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  sx?: SxProps<Theme>;
}) => (
  <TextField
    size="small"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    sx={{ minWidth: { sm: 280 }, ...sx }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Search fontSize="small" />
        </InputAdornment>
      ),
      endAdornment: value ? (
        <InputAdornment position="end">
          <IconButton size="small" onClick={() => onChange("")} aria-label="Clear search">
            <Clear fontSize="small" />
          </IconButton>
        </InputAdornment>
      ) : undefined,
    }}
  />
);

export const EmptyState = ({
  icon,
  title,
  message,
  action,
}: {
  icon: ReactNode;
  title: string;
  message?: ReactNode;
  action?: ReactNode;
}) => (
  <Stack alignItems="center" justifyContent="center" textAlign="center" gap={1} py={6} px={2}>
    <Box sx={{ color: "text.disabled", "& svg": { fontSize: 40 } }}>{icon}</Box>
    <Typography fontWeight={600}>{title}</Typography>
    {message && (
      <Typography fontSize={13} color="text.secondary" maxWidth={420}>
        {message}
      </Typography>
    )}
    {action && <Box mt={1}>{action}</Box>}
  </Stack>
);

export const ErrorState = ({ error, fallback, onRetry }: { error: unknown; fallback: string; onRetry: () => void }) => (
  <Alert
    severity="error"
    action={
      <Button color="inherit" size="small" onClick={onRetry}>
        Retry
      </Button>
    }
  >
    {getErrorMessage(error, fallback)}
  </Alert>
);

// Coloured initials avatar; the colour is stable per name.
const AVATAR_COLORS = ["#1f7aec", "#7c3aed", "#0f9d58", "#e8710a", "#d93025", "#00897b", "#5c6bc0", "#c2185b"];
export const InitialsAvatar = ({ name, size = 36 }: { name?: string; size?: number }) => {
  const text = (name ?? "").trim();
  const initials =
    text
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";
  const color = AVATAR_COLORS[text.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
  return (
    <Avatar sx={{ width: size, height: size, fontSize: size * 0.38, fontWeight: 700, bgcolor: `${color}1a`, color }}>
      {initials}
    </Avatar>
  );
};

export const gridSx: SxProps<Theme> = {
  border: "1px solid #e6e9ef",
  borderRadius: 2,
  "& .MuiDataGrid-columnHeaders": { bgcolor: "#f6f8fa" },
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, fontSize: 12, color: "#5b6573" },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
    { outline: "none" },
  "& .MuiDataGrid-row:hover": { bgcolor: "#f8faff" },
  "& .MuiDataGrid-cell": { py: 1, alignItems: "center" },
  "& .MuiDataGrid-overlayWrapper": { minHeight: 240 },
};

// Case-insensitive "every word matches somewhere" search.
export const matchesSearch = (values: unknown[], query: string) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const text = values.map((v) => String(v ?? "")).join(" | ").toLowerCase();
  return q.split(/\s+/).every((t) => text.includes(t));
};
